import { useEffect, useRef } from 'react'
import { useMantle } from '@heymantle/react'
import { useAppDispatch, useAppSelector } from '@/store'
import { setQuota, selectQuota } from '@/store/slices/authSlice'
import { syncQuota } from '@/api/billingApi'
import type { QuotaInfo } from '@/features/auth/types'
import { store } from '@/store'

/**
 * Watches Mantle customer data and reconciles with Redux + backend.
 *
 * - Updates Redux `limit` and billing cycle from Mantle (source of truth for plan data)
 * - Does NOT touch `used` — the backend tracks actual response count, not Mantle
 * - Calls backend sync on plan change AND when quota is exceeded
 */
export const useMantleQuotaSync = () => {
    const dispatch = useAppDispatch()
    const { customer, subscription } = useMantle()
    const lastFingerprintRef = useRef<string | null>(null)
    const backendLimitSnapshotRef = useRef<number | undefined>(undefined)
    const exceededSyncedRef = useRef(false)

    // Stable primitives as deps
    const planId = subscription?.plan?.id ?? null
    const periodStart = subscription?.currentPeriodStart ?? null

    // Watch quota from Redux (/auth/me updates this every 5s)
    const quota = useAppSelector(selectQuota)
    const quotaUsed = quota?.used ?? 0
    const quotaLimit = quota?.limit ?? 0
    const quotaExceeded = quota?.exceeded ?? false

    // Refs for latest Mantle data
    const customerRef = useRef(customer)
    const subscriptionRef = useRef(subscription)
    customerRef.current = customer
    subscriptionRef.current = subscription

    // ── Effect 1: Plan change sync ──────────────────────────────────
    useEffect(() => {
        const sub = subscriptionRef.current
        const cust = customerRef.current

        if (!cust || !sub || !planId) return

        // Extract quota limit — customer.features is the resolved view
        // (plan defaults + customer-level overrides), so prefer it over
        // subscription features which only reflect the raw plan.
        const custFeatures = (cust as any).features
        const subFeatures = sub.features || sub.plan?.features
        const limitFeature =
            custFeatures?.survey_responses ??
            custFeatures?.responses_per_survey ??
            subFeatures?.survey_responses ??
            subFeatures?.responses_per_survey ??
            null
        const newLimit =
            typeof limitFeature?.value === 'number' ? limitFeature.value : null

        if (newLimit == null) return

        const billingCycleStart = sub.currentPeriodStart ?? null
        const billingCycleEnd = sub.currentPeriodEnd ?? null

        const currentQuota: QuotaInfo | null = store.getState().auth.quota

        // Wait for /auth/me to populate quota first — otherwise we'd
        // dispatch used: 0 which hides the banner on initial load.
        // Don't set fingerprint so this retries when quotaUsed changes
        // (which happens when /auth/me resolves).
        if (!currentQuota) return

        // Fingerprint prevents duplicate dispatches
        const fingerprint = `${planId}_${newLimit}_${billingCycleStart}`
        if (lastFingerprintRef.current === fingerprint) return
        lastFingerprintRef.current = fingerprint

        // Snapshot backend limit on first run
        if (backendLimitSnapshotRef.current === undefined && currentQuota.limit != null) {
            backendLimitSnapshotRef.current = currentQuota.limit
        }

        // Update Redux — only limit and billing cycle from Mantle
        // Keep `used` from backend (the real response count)
        const limitChanged = currentQuota.limit !== newLimit
        const cycleChanged = currentQuota.billingCycleStart !== billingCycleStart

        if (limitChanged || cycleChanged) {
            dispatch(
                setQuota({
                    limit: newLimit,
                    used: currentQuota.used,
                    exceeded: currentQuota.used >= newLimit,
                    billingCycleStart,
                    billingCycleEnd,
                })
            )
        }

        // Sync to backend only when Mantle's limit differs from what
        // the backend originally reported (plan was changed)
        const backendLimit = backendLimitSnapshotRef.current
        const backendOutOfSync = backendLimit != null && backendLimit !== newLimit

        if (backendOutOfSync) {
            // Reset exceeded sync flag — new plan means fresh check
            exceededSyncedRef.current = false
            const plan = sub.plan
            syncQuota({
                mantleCustomerId: (cust as any).id || '',
                mantleSubscriptionId: sub.id || '',
                planId: plan?.id || '',
                planName: plan?.name || '',
                planAmount: plan?.amount ?? 0,
                planInterval: plan?.interval || '',
                monthlyResponseLimit: newLimit,
                billingCycleStart,
                billingCycleEnd,
                billingCycleAnchor: sub.billingCycleAnchor ?? null,
                subscriptionActive: sub.active ?? false,
            }).catch((err) =>
                console.warn('[MantleQuotaSync] Backend sync failed (non-critical)', err)
            )
        }
    }, [planId, periodStart, quotaUsed, dispatch])

    // ── Effect 2: Quota exceeded detection ──────────────────────────
    // Watches /auth/me quota. When used >= limit but backend hasn't
    // flagged exceeded yet, call sync to set quota_exceeded on backend.
    useEffect(() => {
        const sub = subscriptionRef.current
        const cust = customerRef.current

        if (!cust || !sub || !quotaLimit) return

        const isExceeded = quotaUsed >= quotaLimit
        if (!isExceeded) {
            // Usage dropped below limit (e.g. new billing cycle) — reset flag
            exceededSyncedRef.current = false
            return
        }

        // Already exceeded and backend knows (exceeded: true from /auth/me)
        if (quotaExceeded) {
            exceededSyncedRef.current = true
            return
        }

        // Exceeded but backend doesn't know yet — sync once
        if (exceededSyncedRef.current) return
        exceededSyncedRef.current = true

        const plan = sub.plan
        const billingCycleStart = sub.currentPeriodStart ?? null
        const billingCycleEnd = sub.currentPeriodEnd ?? null

        console.info('[MantleQuotaSync] Quota exceeded detected, syncing to backend', {
            used: quotaUsed,
            limit: quotaLimit,
        })

        syncQuota({
            mantleCustomerId: (cust as any).id || '',
            mantleSubscriptionId: sub.id || '',
            planId: plan?.id || '',
            planName: plan?.name || '',
            planAmount: plan?.amount ?? 0,
            planInterval: plan?.interval || '',
            monthlyResponseLimit: quotaLimit,
            billingCycleStart,
            billingCycleEnd,
            billingCycleAnchor: sub.billingCycleAnchor ?? null,
            subscriptionActive: sub.active ?? false,
        }).catch((err) =>
            console.warn('[MantleQuotaSync] Exceeded sync failed (non-critical)', err)
        )

        // Update Redux immediately so UI reflects exceeded state
        dispatch(
            setQuota({
                limit: quotaLimit,
                used: quotaUsed,
                exceeded: true,
                billingCycleStart,
                billingCycleEnd,
            })
        )
    }, [quotaUsed, quotaLimit, quotaExceeded, dispatch])
}
